import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { useTranslation } from 'react-i18next';
import { Input, Select, SelectItem, Chip, Spinner, Tooltip } from '@nextui-org/react';
import type { RowClickedEvent } from 'ag-grid-community';
import { ColDef, GridReadyEvent, ICellRendererParams } from 'ag-grid-community';
import { CtyButton, useAgGridStatePersistence, useHasApplicationResource } from '@circuitry-ai/doc-ui';
import { Icon } from '@iconify/react';
import {
  type DataConnector,
  type GetDataConnectorsQueryArg,
  useGetDataConnectorsQuery,
  useGetCredentialsListQuery,
} from '@circuitry-ai/doc-data';
import { ConnectorForm } from './connector-form';

const SEARCH_DEBOUNCE_MS = 400;

function statusCellRenderer(params: ICellRendererParams<DataConnector>) {
  const s = String(params.value ?? '');
  const active = s.toLowerCase() === 'active';
  const value = [{ key: 'active', label: 'Active' }, { key: 'Inactive', label: 'In Active' }].find((v) => v.key === s)?.label ?? s;
  return (
    <Chip size="sm" color={active ? 'success' : 'warning'} variant="flat">
      {value}
    </Chip>
  );
}

export default function ConnectorsSettings() {
  const { t } = useTranslation();
  const hasIntegrationsAccess = useHasApplicationResource('integrations');
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('active');
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editingConnector, setEditingConnector] = useState<DataConnector | null>(null);

  useEffect(() => {
    const id = globalThis.setTimeout(() => setDebouncedSearch(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => globalThis.clearTimeout(id);
  }, [searchInput]);

  const listQueryArg = useMemo((): GetDataConnectorsQueryArg => {
    const arg: GetDataConnectorsQueryArg = {};
    if (debouncedSearch) {
      arg.search = debouncedSearch;
    }
    if (statusFilter === 'active' || statusFilter === 'inactive') {
      arg.status = statusFilter;
    }
    return arg;
  }, [debouncedSearch, statusFilter]);

  const { data: listData, isLoading, isFetching, refetch } = useGetDataConnectorsQuery(listQueryArg, {
    refetchOnMountOrArgChange: true,
  });
  const { data: credentialsList = [], isLoading: credentialsLoading } = useGetCredentialsListQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  const connectors = listData?.connectors ?? [];

  const credentialNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of credentialsList) {
      if (c?.credential_id) {
        m.set(c.credential_id, c.name ?? c.credential_id);
      }
    }
    return m;
  }, [credentialsList]);

  const statusFilterSelectItems = useMemo(
    () => [
      { key: 'active', label: t('Active') },
      { key: 'inactive', label: t('In Active') },
    ],
    [t]
  );

  const totalCount =
    listData?.total_all_connections ?? listData?.total_connections ?? connectors.length;
  const activeCount =
    listData?.active_connections ??
    connectors.filter((c: DataConnector) => (c.status ?? '').toLowerCase() === 'active').length;
  const inactiveCount =
    listData?.inactive_connections ??
    connectors.filter((c: DataConnector) => (c.status ?? '').toLowerCase() !== 'active').length;

  const gridStyle = useMemo(
    () =>
      ({
        height: '100%',
        width: '100%',
        '--ag-border-color': '#e5e7eb',
        '--ag-cell-horizontal-border': '1px solid var(--ag-border-color)',
      }) as React.CSSProperties,
    []
  );

  const credentialsCellRenderer = useCallback(
    (params: ICellRendererParams<DataConnector>) => {
      const row = params.data;
      if (!row) return null;
      const name = credentialNameById.get(row.credential_id) ?? '';
      return (
        <div className="py-1">
          <div className="font-semibold text-sm">{name || row.credential_id}</div>
          <div className="text-xs text-default-500">{row.credential_id}</div>
        </div>
      );
    },
    [credentialNameById]
  );

  const columnDefs = useMemo<ColDef<DataConnector>[]>(
    () => [
      {
        headerName: t('Connector name'),
        field: 'connector_name',
        flex: 1,
        minWidth: 180,
        cellClass: 'font-semibold',
      },
      {
        headerName: t('Description'),
        field: 'description',
        flex: 1.2,
        minWidth: 200,
        valueFormatter: (p) => (p.value == null ? '' : String(p.value)),
      },
      {
        headerName: t('Type'),
        field: 'connector_type',
        minWidth: 130,
      },
      {
        headerName: t('Status'),
        field: 'status',
        minWidth: 120,
        cellRenderer: statusCellRenderer,
      },
      {
        headerName: t('Credentials'),
        field: 'credential_id',
        flex: 1,
        minWidth: 200,
        cellRenderer: credentialsCellRenderer,
        sortable: false,
      },
    ],
    [t, credentialsCellRenderer]
  );

  const defaultColDef = useMemo<ColDef>(
    () => ({
      resizable: true,
      sortable: true,
      suppressMovable: false,
    }),
    []
  );

  const { augmentGridReady, mergeColumnDefs, suppressDragLeaveHidesColumns } =
    useAgGridStatePersistence('connectors-settings-table');

  const onGridReady = useCallback(
    (params: GridReadyEvent) => {
      augmentGridReady(params, (p) => {
        p?.api?.sizeColumnsToFit?.();
      });
    },
    [augmentGridReady]
  );

  const columnDefsMerged = useMemo(() => mergeColumnDefs(columnDefs), [columnDefs, mergeColumnDefs]);

  const openNewSheet = () => {
    setEditingConnector(null);
    setSheetOpen(true);
  };

  const openEditSheet = useCallback((row: DataConnector) => {
    setEditingConnector(row);
    setSheetOpen(true);
  }, []);

  const closeSheet = () => {
    setSheetOpen(false);
    setEditingConnector(null);
  };

  const handleRowClicked = useCallback(
    (event: RowClickedEvent<DataConnector>) => {
      if (event.data) {
        openEditSheet(event.data);
      }
    },
    [openEditSheet]
  );

  const loading = isLoading || isFetching;

  return (
    <div className="flex flex-col gap-4 max-w-[1200px] relative pt-3">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t('Connectors')}</h1>
          <p className="text-sm text-default-500 mt-1 max-w-xl">
            {t('Configure data source connections and link source credentials from the vault.')}
          </p>
        </div>
        <CtyButton
          color="primary"
          variant="solid"
          radius="md"
          className="font-semibold shrink-0"
          startContent={<Icon icon="fa6-solid:plus" width={16} />}
          onPress={openNewSheet}
          isDisabled={!hasIntegrationsAccess}
        >
          {t('New Connector')}
        </CtyButton>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="rounded-xl border border-default-200 bg-content1 p-4 shadow-sm">
          <div className="text-[11px] uppercase tracking-wide text-default-500 font-medium">{t('Total connectors')}</div>
          <div className="text-3xl font-bold mt-1">{loading ? <Spinner size="sm" /> : <span>{totalCount}</span>}</div>
          <Tooltip content={t('Includes connectors linked to each credential')} placement="bottom-start">
            <Chip
              size="sm"
              variant="flat"
              className="mt-2 text-xs max-w-full"
              color="default"
              classNames={{ content: 'truncate max-w-[270px]' }}
            >
              {t('Includes connectors linked to each credential')}
            </Chip>
          </Tooltip>
        </div>
        <div className="rounded-xl border border-default-200 bg-content1 p-4 shadow-sm">
          <div className="text-[11px] uppercase tracking-wide text-default-500 font-medium">{t('Active')}</div>
          <div className="text-3xl font-bold mt-1 text-success">{loading ? <Spinner size="sm" /> : <span>{activeCount}</span>}</div>
          <Tooltip content={t('Currently active connections')} placement="bottom-start">
            <Chip size="sm" variant="flat" className="mt-2 text-xs" color="success">
              {t('Currently active connections')}
            </Chip>
          </Tooltip>
          
        </div>
        <div className="rounded-xl border border-default-200 bg-content1 p-4 shadow-sm">
          <div className="text-[11px] uppercase tracking-wide text-default-500 font-medium">{t('In Active')}</div>
          <div className="text-3xl font-bold mt-1 text-default-700">{loading ? <Spinner size="sm" /> : <span>{inactiveCount}</span>}</div>
          <Tooltip content={t('Inactive or paused connectors')} placement="bottom-start">
            <Chip size="sm" variant="flat" className="mt-2 text-xs" color="warning">
              {t('Inactive or paused connectors')}
            </Chip>
          </Tooltip>
        </div>
      </div>

      <div className="rounded-xl border border-default-200 bg-content1 shadow-sm overflow-hidden">
        <div className="flex flex-col md:flex-row flex-wrap gap-2 p-3 border-b border-default-200">
          <Input
            aria-label={t('Search connectors')}
            placeholder={t('Search connectors...')}
            value={searchInput}
            onValueChange={setSearchInput}
            className="max-w-md"
            size="sm"
            variant="bordered"
            radius="md"
            startContent={<Icon icon="solar:magnifer-linear" className="text-default-400" width={18} />}
          />
          <Select
            aria-label={t('Status filter')}
            placeholder={t('Status')}
            items={statusFilterSelectItems}
            selectedKeys={statusFilter === 'all' ? new Set<string>() : new Set([statusFilter])}
            onSelectionChange={(keys) => {
              const v = Array.from(keys)[0] as string | undefined;
              setStatusFilter(v === 'active' || v === 'inactive' ? v : 'all');
            }}
            className="w-full md:w-48"
            size="sm"
            variant="bordered"
            renderValue={() =>
              statusFilter === 'all' ? (
                <span className="text-default-400 text-sm">{t('Status')}</span>
              ) : (
                <span className="text-sm">
                  {statusFilter === 'active' ? t('Active') : t('Inactive')}
                </span>
              )
            }
          >
            {(item) => (
              <SelectItem key={item.key} textValue={item.label}>
                {item.label}
              </SelectItem>
            )}
          </Select>
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <Spinner size="lg" />
          </div>
        ) : (
          <div className="ag-theme-alpine p-2 connectors-grid-clickable-rows" style={gridStyle}>
            <AgGridReact<DataConnector>
              suppressDragLeaveHidesColumns={suppressDragLeaveHidesColumns}
              rowData={connectors}
              columnDefs={columnDefsMerged}
              defaultColDef={defaultColDef}
              headerHeight={45}
              rowHeight={45}
              domLayout="autoHeight"
              onGridReady={onGridReady}
              onRowClicked={handleRowClicked}
              pagination
              paginationPageSize={10}
              paginationPageSizeSelector={[10, 20, 50]}
              suppressCellFocus
              getRowId={(p) => p.data.connector_id}
            />
          </div>
        )}
      </div>

      <ConnectorForm
        isOpen={sheetOpen}
        onClose={closeSheet}
        editingConnector={editingConnector}
        credentialsList={credentialsList}
        credentialsLoading={credentialsLoading}
        onSaved={() => {
          refetch();
        }}
      />
    </div>
  );
}
